import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import type { Role, SessionUser } from "@/api/auth";
import { deleteUser, getUserDetail, updateUser, type UpdateUserInput } from "@/api/users";
import { Badge, Button } from "@/components";
import { useLang } from "@/i18n/LanguageContext";
import { canManage, roleLabel, rolesBelow, type TenantRole } from "./roles";

const ROLE_TONE: Record<string, "ok" | "warn" | "neutral"> = {
  org_admin: "warn",
  manager: "neutral",
  employee: "ok",
  platform_admin: "warn",
};

interface Props {
  user: Pick<SessionUser, "id" | "email" | "full_name" | "role">;
  actorRole: Role;
  onChanged: () => void;
}

const EMPTY_PROFILE = { department: "", job_title: "", team: "", location: "", employment_type: "", seniority: "", start_date: "" };

export function ManageableUserRow({ user, actorRole, onChanged }: Props) {
  const { t } = useLang();
  const manage = canManage(actorRole, user.role);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [fullName, setFullName] = useState(user.full_name ?? "");
  const [role, setRole] = useState<TenantRole>(user.role as TenantRole);
  const [password, setPassword] = useState("");
  const [profile, setProfile] = useState({ ...EMPTY_PROFILE });

  const detail = useQuery({
    queryKey: ["user-detail", user.id],
    queryFn: ({ signal }) => getUserDetail(user.id, signal),
    enabled: editing,
  });

  useEffect(() => {
    if (detail.data) {
      setFullName(detail.data.full_name ?? "");
      setRole(detail.data.role as TenantRole);
      setProfile({ ...EMPTY_PROFILE, ...(detail.data.profile ?? {}) });
    }
  }, [detail.data?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: () => {
      const payload: UpdateUserInput = { full_name: fullName, role };
      if (password) payload.password = password;
      if (role === "employee" || role === "manager") payload.profile = profile;
      return updateUser(user.id, payload);
    },
    onSuccess: () => { setEditing(false); setPassword(""); setError(null); onChanged(); },
    onError: (e) => setError(e instanceof Error ? e.message : t("Opslaan mislukt", "Could not save")),
  });

  const remove = useMutation({
    mutationFn: () => deleteUser(user.id),
    onSuccess: onChanged,
    onError: (e) => setError(e instanceof Error ? e.message : t("Verwijderen mislukt", "Could not delete")),
  });

  function onDelete() {
    const msg = t(
      `Account van ${user.full_name || user.email} verwijderen? Dit kan niet ongedaan worden.`,
      `Delete the account of ${user.full_name || user.email}? This cannot be undone.`,
    );
    if (window.confirm(msg)) remove.mutate();
  }

  const assignable = rolesBelow(actorRole);

  return (
    <li style={{ flexDirection: "column", alignItems: "stretch", gap: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <span>
          <strong>{user.full_name || user.email}</strong>{" "}
          <span style={{ color: "var(--grey-text)", fontSize: 13 }}>· {user.email}</span>
        </span>
        <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {actorRole !== "platform_admin" && (user.role === "employee" || user.role === "manager") && (
            <Link to={`/app/employees/${user.id}/program`} style={{ fontSize: 13, color: "var(--amber-dark)", fontWeight: 600 }}>{t("traject", "journey")} ↗</Link>
          )}
          <Badge tone={ROLE_TONE[user.role]}>{roleLabel(user.role, t)}</Badge>
          {manage && (
            <>
              <button type="button" className="btn btn-outline" style={{ padding: "6px 12px", fontSize: 13 }} onClick={() => setEditing((v) => !v)}>
                {editing ? t("Sluiten", "Close") : t("Bewerk", "Edit")}
              </button>
              <button type="button" className="icon-btn" title={t("Verwijderen", "Delete")} onClick={onDelete}>×</button>
            </>
          )}
        </span>
      </div>

      {editing && manage && (
        <div style={{ background: "var(--bg)", borderRadius: 8, padding: 16 }}>
          {error && <div className="form-error">{error}</div>}
          {detail.isLoading ? (
            <p style={{ color: "var(--grey-text)" }}>{t("Laden…", "Loading…")}</p>
          ) : (
            <>
              <div className="field">
                <label>{t("Naam", "Name")}</label>
                <input value={fullName} onChange={(e) => setFullName(e.target.value)} />
              </div>
              <div className="field">
                <label>{t("Rol", "Role")}</label>
                <select value={role} onChange={(e) => setRole(e.target.value as TenantRole)}>
                  {assignable.map((r) => <option key={r} value={r}>{roleLabel(r, t)}</option>)}
                </select>
              </div>
              <div className="field">
                <label>{t("Nieuw wachtwoord (optioneel)", "New password (optional)")}</label>
                <input type="password" autoComplete="new-password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("leeg laten = ongewijzigd", "leave empty = unchanged")} />
              </div>
              {(role === "employee" || role === "manager") && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <Field label={t("Functie", "Job title")} value={profile.job_title} onChange={(v) => setProfile({ ...profile, job_title: v })} />
                  <Field label={t("Afdeling", "Department")} value={profile.department} onChange={(v) => setProfile({ ...profile, department: v })} />
                  <Field label={t("Team", "Team")} value={profile.team} onChange={(v) => setProfile({ ...profile, team: v })} />
                  <Field label={t("Locatie", "Location")} value={profile.location} onChange={(v) => setProfile({ ...profile, location: v })} />
                  <Field label={t("Contracttype", "Employment type")} value={profile.employment_type} onChange={(v) => setProfile({ ...profile, employment_type: v })} />
                  <Field label={t("Startdatum", "Start date")} type="date" value={profile.start_date ?? ""} onChange={(v) => setProfile({ ...profile, start_date: v })} />
                </div>
              )}
              <div style={{ marginTop: 8 }}>
                <Button onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? t("Opslaan…", "Saving…") : t("Opslaan", "Save")}</Button>
              </div>
            </>
          )}
        </div>
      )}
    </li>
  );
}

function Field({ label, value, onChange, type = "text" }: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <div className="field" style={{ marginBottom: 0 }}>
      <label>{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
